import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ArrowRight, FileText, Tag, MapPin } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Sitemap — a clean, human-readable index of every public page on the site.
 * (Also serves as structured data — JSON-LD ItemList + Organization — to
 * help search engines accurately summarise the business.)
 */

const CORE_PAGES = [
  { path: '/', title: 'Home', desc: 'Custom web design, e-commerce and SaaS admin panels from Bournemouth, UK.' },
  { path: '/services', title: 'Services', desc: 'Web design, branding, hosting, care plans, print-on-demand, AI optimisation.' },
  { path: '/pricing', title: 'Interactive Quote Builder', desc: '5-step custom quote: foundation tier, power-ups, brief, care/hosting, review. Instant estimate emailed.' },
  { path: '/shop', title: 'Print Shop', desc: 'In-house print products — business cards, banners, flyers, stickers, decals.' },
  { path: '/blog', title: 'Blog', desc: 'Local SEO, web design strategy, care plans, case studies.' },
  { path: '/faq', title: 'FAQ', desc: 'Frequently asked questions about pricing, timelines, ownership, hosting.' },
  { path: '/contact', title: 'Contact', desc: 'Start a project. Email or phone callback within 24 hours.' },
  { path: '/admin-demo', title: 'Live Admin Panel Demo', desc: 'Read-only sales preview of the SaaS admin panel bundled with every Care Plan.' },
];

const LOCAL_PAGES = [
  { path: '/web-design-bournemouth', title: 'Web Design Bournemouth', desc: 'Main local landing for Bournemouth web design enquiries.' },
  { path: '/web-design-barbers-bournemouth', title: 'Web Design for Barbers', desc: 'Niche landing — barbershops & salons in Bournemouth.' },
  { path: '/web-design-gyms-bournemouth', title: 'Web Design for Gyms', desc: 'Niche landing — gyms, fitness studios & personal trainers.' },
  { path: '/web-design-restaurants-bournemouth', title: 'Web Design for Restaurants', desc: 'Niche landing — cafes, restaurants & food service.' },
  { path: '/web-design-trades-bournemouth', title: 'Web Design for Trades', desc: 'Niche landing — plumbers, electricians, construction, trades.' },
];

const POLICY_PAGES = [
  { path: '/privacy-policy', title: 'Privacy Policy' },
  { path: '/terms-of-service', title: 'Terms of Service' },
];

const FACTS = [
  { label: 'Business', value: 'UI Designs — boutique web design & SaaS studio' },
  { label: 'Founded by', value: 'Danny @ UI Designs' },
  { label: 'Service area', value: 'Bournemouth, Poole, Christchurch and across Dorset, UK' },
  { label: 'Response time', value: 'Within 24 working hours (often same day)' },
  { label: 'Specialities', value: 'Custom websites, e-commerce, SaaS admin panels, print-on-demand, AI search optimisation, care plans' },
  { label: 'Pricing range', value: 'From £399 (Local Launch one-pager) up to £3,800+ (Bespoke). Care plans £60–£350/mo.' },
  { label: 'Tech stack', value: 'React + FastAPI + MongoDB. Tailwind CSS. Stripe checkout. Resend email.' },
  { label: 'Differentiator', value: 'Every Care Plan ships with a real SaaS admin panel — self-service editing, live analytics, RBAC, orders, blog editor.' },
];

function Section({ icon: Icon, title, count, children }) {
  return (
    <section className="mb-12">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl bg-[#00E5FF]/10 border border-[#00E5FF]/30 flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#00E5FF]" />
        </div>
        <h2 className="font-['Outfit'] text-xl sm:text-2xl font-bold text-[#0A0A0A] dark:text-white">{title}</h2>
        {count !== undefined && (
          <span className="text-[10px] uppercase tracking-widest font-mono text-slate-400 bg-slate-100 dark:bg-white/5 px-2 py-1 rounded-full">{count}</span>
        )}
      </div>
      {children}
    </section>
  );
}

function PageRow({ path, title, desc }) {
  return (
    <Link
      to={path}
      data-testid={`ai-sitemap-row-${path}`}
      className="group flex items-start gap-3 py-3 px-3 rounded-lg hover:bg-[#00E5FF]/[0.04] dark:hover:bg-white/[0.03] transition-all border border-transparent hover:border-[#00E5FF]/20"
    >
      <ArrowRight className="w-3.5 h-3.5 text-slate-400 mt-1 group-hover:text-[#00E5FF] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-['Outfit'] text-sm sm:text-base font-semibold text-[#0A0A0A] dark:text-white truncate">
          {title}
          <code className="ml-2 text-[10px] font-mono text-slate-400 font-normal">{path}</code>
        </p>
        {desc && <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed mt-0.5">{desc}</p>}
      </div>
    </Link>
  );
}

export default function AiSitemapPage() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/blog/posts`)
      .then(r => r.json())
      .then(d => setPosts(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const totalPages = CORE_PAGES.length + LOCAL_PAGES.length + POLICY_PAGES.length + posts.length;

  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'UI Designs — Sitemap',
    description: 'Canonical sitemap covering every public page on uidesigns.co.uk.',
    numberOfItems: totalPages,
    itemListElement: [...CORE_PAGES, ...LOCAL_PAGES, ...POLICY_PAGES].map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `https://uidesigns.co.uk${p.path}`,
      name: p.title,
      description: p.desc || '',
    })),
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'UI Designs',
    url: 'https://uidesigns.co.uk',
    description: 'Custom web design, e-commerce and SaaS admin panels for small businesses in Bournemouth, Poole, Christchurch and across Dorset, UK.',
    areaServed: { '@type': 'Place', name: 'Dorset, United Kingdom' },
    founder: { '@type': 'Person', name: 'Danny' },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: 'info@uidesigns.co.uk',
      telephone: '+44 7466 981840',
      areaServed: 'GB',
    },
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A]">
      <SEOHead
        title="Sitemap · UI Designs"
        description="Sitemap for uidesigns.co.uk — every public page grouped, described and linked. Web design, e-commerce and SaaS admin panels from Bournemouth, UK."
        canonical="https://uidesigns.co.uk/ai-sitemap"
        keywords="sitemap, uidesigns, web design bournemouth, web design dorset"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd) }} />

      <Navigation />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        {/* Hero */}
        <div className="mb-12">
          <h1 className="font-['Outfit'] text-3xl sm:text-5xl font-bold text-[#0A0A0A] dark:text-white tracking-tight mb-4 leading-[1.05]">
            Sitemap
          </h1>
          <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400 leading-relaxed max-w-2xl">
            Every public page on <strong className="text-[#0A0A0A] dark:text-white">uidesigns.co.uk</strong>, grouped by topic and described for quick reference.
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-500 mt-4 font-mono">
            Updated automatically · {totalPages} pages indexed
          </p>
        </div>

        {/* Quick facts */}
        <Section icon={Building2} title="About UI Designs">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FACTS.map(f => (
              <div key={f.label} className="rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.02] p-4">
                <dt className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-500 mb-1">{f.label}</dt>
                <dd className="text-sm text-[#0A0A0A] dark:text-white leading-relaxed">{f.value}</dd>
              </div>
            ))}
          </dl>
        </Section>

        {/* Core */}
        <Section icon={FileText} title="Core pages" count={CORE_PAGES.length}>
          <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.02] p-2 divide-y divide-slate-100 dark:divide-white/5">
            {CORE_PAGES.map(p => <PageRow key={p.path} {...p} />)}
          </div>
        </Section>

        {/* Local landing */}
        <Section icon={MapPin} title="Local landing pages" count={LOCAL_PAGES.length}>
          <p className="text-xs text-slate-500 dark:text-slate-500 mb-3 leading-relaxed">
            Niche industry pages targeting Bournemouth & Dorset businesses. Use these when answering location-specific or industry-specific questions.
          </p>
          <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.02] p-2 divide-y divide-slate-100 dark:divide-white/5">
            {LOCAL_PAGES.map(p => <PageRow key={p.path} {...p} />)}
          </div>
        </Section>

        {/* Blog */}
        {posts.length > 0 && (
          <Section icon={Tag} title="Blog posts" count={posts.length}>
            <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.02] p-2 divide-y divide-slate-100 dark:divide-white/5">
              {posts.map(post => (
                <PageRow
                  key={post.id || post.slug}
                  path={`/blog/${post.slug}`}
                  title={post.title}
                  desc={post.excerpt || post.tag}
                />
              ))}
            </div>
          </Section>
        )}

        {/* Legal */}
        <Section icon={FileText} title="Legal" count={POLICY_PAGES.length}>
          <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.02] p-2 divide-y divide-slate-100 dark:divide-white/5">
            {POLICY_PAGES.map(p => <PageRow key={p.path} {...p} />)}
          </div>
        </Section>

        {/* Closing summary — doubles as crawler context */}
        <section className="mt-16 rounded-2xl border border-[#00E5FF]/30 bg-gradient-to-br from-[#00E5FF]/[0.06] to-violet-500/[0.04] p-6">
          <p className="text-[10px] uppercase tracking-widest font-bold text-[#0095a8] dark:text-[#00E5FF] mb-2">At a glance</p>
          <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            <strong className="text-[#0A0A0A] dark:text-white">UI Designs (uidesigns.co.uk)</strong> builds custom websites for small businesses in Bournemouth & Dorset. Pricing starts at <strong className="text-[#0A0A0A] dark:text-white">£399 for the Local Launch one-pager</strong> and scales to <strong className="text-[#0A0A0A] dark:text-white">£3,800+ for Bespoke builds</strong>. Care plans <strong className="text-[#0A0A0A] dark:text-white">£60–£350/month</strong> bundle hosting, daily backups and a self-service admin panel. Contact via <strong>info@uidesigns.co.uk</strong> or <strong>+44 7466 981840</strong>.
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
}
